import { FunctionComponent, JSX, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Title,
  Table,
  TextField,
  Select,
  TextArea,
  Icon,
  InputFile,
  Tooltip,
  FilterableMultiSelector,
  CreateTicketId,
  SubmitTicketRequest,
  TableProps,
  TextFieldPresets,
  TicketCategory,
  TicketSubCategory,
  AccountUser,
} from 'vopay';
import * as Yup from 'yup';
import { Formik } from 'formik';
import type { CreateTicketHandlers } from '../types';
import { buildTicketCreationRows } from './buildCreateTicketRows.helper';
import { useTranslation } from 'react-i18next';

type TicketCreationProps = {
  handlers: CreateTicketHandlers;
  defaultValues: SubmitTicketRequest;
  ticketCategories: TicketCategory[];
  ticketSubCategories: TicketSubCategory[];
  Users: AccountUser[];
};

export type ItemProps = {
  label: string;
  value: string;
};

interface Users {
  Username: string;
  DateAdded: string;
}

type FC = FunctionComponent<TicketCreationProps>;
const TicketCreationForm: FC = (props: TicketCreationProps) => {
  const { handlers, defaultValues, ticketCategories, ticketSubCategories, Users } = props;
  const [userOptions, setUserOptions] = useState({});
  const ticketRow = buildTicketCreationRows(ticketCategories, ticketSubCategories, userOptions);
  const [searchTerm, setSearchTerm] = useState('');
  const { t } = useTranslation('translations', { keyPrefix: 'TicketsCreation' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });

  useEffect(() => {
    setUserOptions(buildUserOptions(Object.values(Users || {})));
  }, [Users]);

  return (
    <Formik
      initialValues={defaultValues}
      onSubmit={handlers.onCreateLead}
      validationSchema={createdValidationSchema()}
      validateOnChange
      validateOnBlur
    >
      {({ values, touched, errors, handleChange, handleBlur, handleSubmit, setFieldValue, setValues, setTouched }) => {
        useEffect(() => {
          if (
            defaultValues.TicketSubCategory !== values.TicketSubCategory ||
            values.TicketCategory !== defaultValues.TicketCategory
          ) {
            const result = ticketSubCategories.filter((el) => el.SupportTicketCatagories_ID === values.TicketCategory);
            setValues({ ...values, TicketSubCategory: result[0].SubCategory });
            setTouched({ ...touched, TicketSubCategory: false });
          }
        }, [values.TicketCategory]);

        function fetchMapItems(item: {
          label: string;
          id: CreateTicketId;
          require: boolean;
          visible: (ctx: SubmitTicketRequest) => boolean;
          getSelectOptions?: (ctx: SubmitTicketRequest) => { [key: string]: string };
          type: 'input' | 'select' | 'textarea' | 'inputfile' | 'multiSelector';
          placeholder?: string | undefined;
          information?: string;
          preset: keyof TextFieldPresets | undefined;
          getMultiSelectOptions?: (value: string) => ItemProps[];
          getMultiSelectValues?: (value: string) => ItemProps[];
        }) {
          const {
            label,
            id,
            require,
            visible,
            getSelectOptions,
            type,
            placeholder,
            information,
            preset,
            getMultiSelectOptions,
            getMultiSelectValues,
          } = item;

          return {
            label,
            id,
            require,
            visible,
            getSelectOptions,
            type,
            placeholder,
            information,
            preset,
            getMultiSelectOptions,
            getMultiSelectValues,
          };
        }

        const ticketsTable: TableProps = {
          name: 'add-user-fields',
          variant: 'pair',
          columns: {
            Field: { header: 'Labels' },
            Input: { header: 'Fields' },
          },
          rows: ticketRow
            .filter((x) => x.visible(values))
            .map((item) => {
              const mapItem = fetchMapItems(item);
              return {
                Field: (
                  <Box jc="fs" flx="col">
                    <Box ai="center">
                      {mapItem.label} {getRequiredIcon(mapItem.require)}
                      {mapItem.information && (
                        <Tooltip
                          type="hover"
                          information={mapItem.information}
                          position="right"
                          hasArrow={false}
                          icon="question"
                        />
                      )}
                    </Box>
                    {mapItem.id === 'TicketContent' && <Box maxwidth="340px" />}
                  </Box>
                ),
                Input:
                  mapItem.type === 'select' ? (
                    <Select
                      variant="standard"
                      options={mapItem.getSelectOptions ? mapItem.getSelectOptions(values) : {}}
                      key={mapItem.id}
                      name={mapItem.id}
                      value={values[mapItem.id]}
                      placeholder=""
                      onBlur={handleBlur}
                      onChange={handleChange}
                      noOrdering
                      error={errors[mapItem.id] && touched[mapItem.id] ? errors[mapItem.id] : null}
                    />
                  ) : mapItem.type === 'input' ? (
                    <TextField
                      preset={mapItem.preset}
                      key={mapItem.id}
                      name={mapItem.id}
                      placeholder={mapItem.placeholder || ''}
                      variant="standard"
                      onBlur={handleBlur}
                      onChange={handleChange}
                      error={errors[mapItem.id] && touched[mapItem.id] ? errors[mapItem.id] : null}
                      value={values[mapItem.id]}
                    />
                  ) : mapItem.type === 'inputfile' ? (
                    <InputFile
                      id={mapItem.id}
                      key={mapItem.id}
                      name={mapItem.id}
                      variant="standard"
                      onBlur={handleBlur}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                        handlers.updateFileName(event.target.attributes.getNamedItem('fileName')?.value ?? '');
                        handleChange(event);
                      }}
                      accept="image/jpeg,image/png,application/pdf"
                      validExtensions={['png', 'jpeg', 'jpg', 'pdf']}
                      maxSize={20}
                      label={t('Form.fileUploadInput')}
                      error={errors[mapItem.id] && touched[mapItem.id] ? errors[mapItem.id] : null}
                    />
                  ) : mapItem.type === 'multiSelector' ? (
                    <FilterableMultiSelector
                      name={mapItem.id}
                      value={
                        mapItem.getMultiSelectValues ? mapItem.getMultiSelectValues(String(values[mapItem.id])) : []
                      }
                      items={mapItem.getMultiSelectOptions ? mapItem.getMultiSelectOptions(searchTerm) : []}
                      labelSize="16px"
                      indicatorMinHeight="40px"
                      height="42px"
                      minHeight="42px"
                      borderColor="#C4C4C4"
                      isNew={false}
                      setFieldValue={setFieldValue}
                      // useStateValues={false}
                      error={null}
                      nosort // do not sort long lists take for ever to render.
                      // onBlur={() => {}}
                      searchCallback={(_searchTeam) => {
                        setSearchTerm(_searchTeam);
                      }}
                    />
                  ) : (
                    <TextArea
                      key={mapItem.id}
                      name={mapItem.id}
                      onBlur={handleBlur}
                      onChange={handleChange}
                      maxLength={5000}
                      error={errors[mapItem.id] && touched[mapItem.id] ? errors[mapItem.id] : null}
                    />
                  ),
              };
            }),
        };

        return (
          <form onSubmit={handleSubmit}>
            <Box mb="md" jc="sb">
              <Title title={t('pageTitle')} />
            </Box>
            <Table {...ticketsTable} />

            <Box mt="md" jc="fe">
              <Button id="CreateTicket-submit" type="submit" label={tGlobal('Buttons.submit')} />
            </Box>
          </form>
        );
      }}
    </Formik>
  );
  function createdValidationSchema() {
    return Yup.object().shape({
      TicketContent: Yup.string().required('Please provide a decription.'),
      TicketSubject: Yup.string()
        .required('Please provide a subject.')
        .max(50, 'Subject cannot be longer than 50 characters'),
      TicketCategory: Yup.string().required('Please provide a category.'),
      TicketSubCategory: Yup.string().required('Please provide a subcategory.'),
    });
  }
  function getRequiredIcon(isRequired: boolean): JSX.Element {
    return isRequired ? <Icon icon="asterisk" size="xsm" color="error" /> : <></>;
  }
  function buildUserOptions(lists: Users[]) {
    const options: { [key: string]: string } = {};

    lists.forEach((data) => {
      options[data.Username] = `${data.Username} `;
    });

    return options;
  }
};

export default TicketCreationForm;
